import React, { useEffect, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  Checkbox,
  Button,
  Divider,
  Select,
  Box,
  Grid,
  MenuItem,
  Dialog,
  DialogContent,
  TextField,
  Paper,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import DialogTitle from '../components/DialogTitle';
import { trackFullStoryEvent } from '../helper';
import { getZ3PConfigValue } from '../helper';
import { apiPut } from '../brainApi';

const useStyles = makeStyles(() => ({
  gridBase: {
    display: 'flex',
    alignItems: 'center',
  },
  smallMarginTop: {
    marginTop: '5px',
  },
  marginTop: {
    marginTop: '20px',
  },
  marginTopAndBottom: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  smallMarginTopAndBottom: {
    marginTop: '5px',
    marginBottom: '5px',
  },
  centerText: {
    display: 'block',
    textAlign: 'center',
  },
  inlineText: {
    display: 'inline',
  },
  normalFontWeight: {
    fontWeight: 'normal',
  },
  actionButtonsContainer: {
    justifyContent: 'center',
    padding: '10px 30px',
  },
  actionButtons: {
    justifyContent: 'center',
    padding: '10px',
  },
  divide: {
    margin: '5px 0 15px 0',
    backgroundColor: '#000',
  },
  policyContainer: {
    padding: '10px',
    backgroundColor: '#f2f2f2',
    width: '100%',
  },
  greyBorder: {
    border: 'solid 1px grey',
    borderRadius: '5px',
    padding: '10px',
  },
  selectedBorder: {
    border: 'solid 3px',
    borderRadius: '5px',
    padding: '10px',
  },
}));

const CancellationModal = ({
  sw,
  showCancellationModal,
  setShowCancellationModal,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const {
    has_credit_card: hasCreditCard,
    date: appointmentDate,
    confirmed_appointment_start_time: startTime,
  } = sw;

  const [isChargingCancellationFee, setIsChargingCancellationFee] = useState(
    false,
  );
  const [cancellationCutOffHours, setCancellationCutOffHours] = useState(null);
  const [
    cancellationFeeAmountDollars,
    setCancellationFeeAmountDollars,
  ] = useState(null);
  const [noShowFeeAmountDollars, setNoShowFeeAmountDollars] = useState(null);

  const [
    selectedCancellationChargeOption,
    setSelectedCancellationChargeOption,
  ] = useState('none');
  const [selectedCancellationReason, setSelectedCancellationReason] = useState(
    null,
  );
  const [otherReasonDetails, setOtherReasonDetails] = useState(null);
  const [noteToCustomer, setNoteToCustomer] = useState(null);
  const [cancellationFeeOptions, setCancellationFeeOptions] = useState([]);
  const [
    sendCancellationNotification,
    setSendCancellationNotification,
  ] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);

  const cancellationReasons = [
    'Date or Time Conflict',
    'Serviced Car Elsewhere',
    'Other',
  ];

  useEffect(() => {
    const {
      is_charging_cancellation_fee: isChargingCancellationFee,
      cancellation_cut_off_hours: cancellationCutOffHours,
      cancellation_fee_amount_dollars: cancellationFeeAmountDollars,
      no_show_fee_amount_dollars: noShowFeeAmountDollars,
      scheduling_system: schedulingSystem,
    } = getZ3PConfigValue();

    setIsChargingCancellationFee(isChargingCancellationFee);
    setCancellationCutOffHours(cancellationCutOffHours);
    setCancellationFeeAmountDollars(cancellationFeeAmountDollars);
    setNoShowFeeAmountDollars(noShowFeeAmountDollars);

    if (
      isChargingCancellationFee &&
      schedulingSystem === 'time_slots' &&
      startTime
    ) {
      const newFeeOptions = [];
      const currentTime = moment();
      const appointmentTime = moment(`${appointmentDate} ${startTime}`);
      const timeDifference = appointmentTime.diff(currentTime);
      const timeDifferenceInHours = moment
        .duration(appointmentTime.diff(currentTime))
        .asHours();
      // If the appointment is within the cancellation cutoff or if the appointment is in the past
      if (
        cancellationCutOffHours >= timeDifferenceInHours ||
        timeDifference < 0
      ) {
        newFeeOptions.push({
          label: `Charge the customer the cancellation fee of $${cancellationFeeAmountDollars}.`,
          type: 'cancellation',
        });
        setSelectedCancellationChargeOption('cancellation');
      }
      if (timeDifference < 0) {
        // Make sure this option is listed first, if valid
        newFeeOptions.unshift({
          label: `Customer did not show. Charge the customer a $${noShowFeeAmountDollars} no show fee.`,
          type: 'noShow',
        });
        setSelectedCancellationChargeOption('noShow');
      }
      // Only add the last option if the appointment qualifies for at least one fee
      if (newFeeOptions.length > 0) {
        newFeeOptions.push({
          label: 'Cancel the appointment without charging the customer a fee.',
          type: 'none',
        });
      }
      setCancellationFeeOptions(newFeeOptions);
    }
  }, []);

  const handleSubmit = () => {
    setIsCancelling(true);
    let feeToCharge = 0;
    if (selectedCancellationChargeOption === 'cancellation')
      feeToCharge = cancellationFeeAmountDollars;
    if (selectedCancellationChargeOption === 'noShow')
      feeToCharge = noShowFeeAmountDollars;

    trackFullStoryEvent('CTA_Appointment_Details_Cancel', {
      appointment_id: sw?.id,
      cancellation_reason: selectedCancellationReason || '',
      fee_charged: feeToCharge,
    });

    const endpoint = `/scheduled-work/${sw.id}`;
    const body = {
      status: 'cancelled',
      cancellation_reason: selectedCancellationReason || '',
      cancellation_reason_details: otherReasonDetails || '',
      cancellation_fee_to_charge: feeToCharge,
      cancellation_note_to_customer: noteToCustomer || '',
      send_cancellation_notification: sendCancellationNotification,
    };

    return apiPut(endpoint, body)
      .then((e) => {
        setIsCancelling(false);
        setShowCancellationModal(false);
        if (typeof e === 'string' && e.startsWith('Error')) {
          notify(e, 'error');
        }

        if (typeof e === 'object' && e.Status === 'Success') {
          // Explicitly look for false (not just falsey) to make sure ChargeSuccess exists and was sent as false
          if (e.ChargeSuccess === false) {
            notify(
              'Appointment cancelled, but card failed to charge. No fee collected.',
            );
          }
          notify('Appointment cancelled successfully');
          refresh();
        }
      })
      .catch(() => setIsCancelling(false));
  };

  return (
    <Dialog
      open={showCancellationModal}
      onClose={() => setShowCancellationModal(false)}
    >
      <Paper>
        <DialogTitle onClose={() => setShowCancellationModal(false)}>
          Cancel Appointment
        </DialogTitle>
        <Divider />
        {isChargingCancellationFee && (
          <DialogContent>
            <Grid container xs={12} className={classes.gridBase}>
              <Box className={classes.policyContainer}>
                <Typography variant="h4">Cancellation Policy</Typography>
                <Typography>
                  If the customer cancels in less than{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {cancellationCutOffHours} hrs
                  </span>{' '}
                  from the appointment start time, they will be charged a{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    ${cancellationFeeAmountDollars}
                  </span>{' '}
                  cancellation fee. No show appointments will incur a{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    ${noShowFeeAmountDollars}
                  </span>{' '}
                  fee.
                </Typography>
              </Box>
            </Grid>

            {hasCreditCard && cancellationFeeOptions.length > 0 && (
              <div className={classes.marginTop}>
                <Grid item xs>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      fullWidth
                      aria-label="cancellation-charge-option"
                      name="cancellation-charge-option"
                      value={selectedCancellationChargeOption}
                      onChange={(e) =>
                        setSelectedCancellationChargeOption(e.target.value)
                      }
                    >
                      {cancellationFeeOptions.map((e) => (
                        <div
                          key={e.label}
                          className={classes.smallMarginTopAndBottom}
                        >
                          <div
                            className={
                              e.type === selectedCancellationChargeOption
                                ? classes.selectedBorder
                                : classes.greyBorder
                            }
                          >
                            <FormControlLabel
                              value={e.type}
                              control={<Radio color="primary" />}
                              label={e.label}
                            />
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </div>
            )}

            {(!hasCreditCard || !cancellationFeeOptions.length) && (
              <div className={classes.marginTop}>
                <Grid container xs={12} className={classes.centerText}>
                  <Typography variant="h6" className={classes.normalFontWeight}>
                    {!hasCreditCard && 'No card on file. '}Cancellation Fee
                    cannot be collected at this time.
                  </Typography>
                </Grid>
              </div>
            )}
          </DialogContent>
        )}

        <DialogContent>
          <Grid container xs={12} className={classes.gridBase}>
            <Typography variant="h4">Leave a note</Typography>
          </Grid>
          <Grid container xs={12} className={classes.smallMarginTop}>
            <TextField
              value={noteToCustomer}
              onChange={(e) => setNoteToCustomer(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Write a note to the customer"
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              className={classes.greyBorder}
            />
          </Grid>
        </DialogContent>

        <DialogContent>
          <Typography inline variant="h4" className={classes.inlineText}>
            Cancellation Reason<span> </span>
          </Typography>
          <Typography inline variant="subtitle1" className={classes.inlineText}>
            (optional)
          </Typography>

          <Typography variant="subtitle2" className={classes.normalFontWeight}>
            This will not be visible to the customer
          </Typography>
        </DialogContent>
        <DialogContent>
          <Grid container xs={12} className={classes.gridBase}>
            <Select
              id="reasons"
              value={selectedCancellationReason}
              onChange={(e) => setSelectedCancellationReason(e.target.value)}
              fullWidth
              className={classes.greyBorder}
              disableUnderline
            >
              {cancellationReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </DialogContent>
        {selectedCancellationReason === 'Other' && (
          <DialogContent>
            <Grid container xs={12} className={classes.gridBase}>
              <TextField
                fullWidth
                multiline
                value={otherReasonDetails}
                onChange={(e) => setOtherReasonDetails(e.target.value)}
                rows={3}
                placeholder="Cancellation reason details"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                className={classes.greyBorder}
              />
            </Grid>
          </DialogContent>
        )}
        <DialogContent>
          <Grid
            container
            xs={12}
            className={[classes.gridBase, classes.centerText]}
          >
            <FormControlLabel
              control={
                <Checkbox
                  onClick={() =>
                    setSendCancellationNotification(
                      !sendCancellationNotification,
                    )
                  }
                  checked={sendCancellationNotification}
                  color="primary"
                />
              }
              label="Send cancellation notification to customer"
            />
          </Grid>
        </DialogContent>
        <DialogActions fullWidth className={classes.actionButtonsContainer}>
          <Button
            fullWidth
            className={classes.actionButtons}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isCancelling
              ? 'Cancelling...'
              : `Cancel Appointment${
                  isChargingCancellationFee &&
                  !!hasCreditCard &&
                  ['cancellation', 'noShow'].includes(
                    selectedCancellationChargeOption,
                  )
                    ? ' & Collect Fee'
                    : ''
                }`}
          </Button>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default CancellationModal;
